import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { addUserToUnit } from '@/api/accounts';
import useUserTitles from '@/hooks/use-user-titles';

const schema = zod.object({
  role_name: zod.string().min(1, { message: 'Role is required' }),
  user_title_id: zod.string().optional(),
});

type Values = zod.infer<typeof schema>;

const unitRoles = [
  { value: 'unit_owner', label: 'Unit Owner' },
  { value: 'unit_tenant', label: 'Unit Tenant' },
  { value: 'unit_resident', label: 'Unit Resident' },
];

interface EditUnitModalProps {
  accountId: string;
  userId: string;
  unitId: string;
  unitLabel: string;
  current: {
    role_name: string;
    user_title_id?: string;
  };
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditUnitModal({
  accountId,
  userId,
  unitId,
  unitLabel,
  current,
  open,
  onClose,
  onSuccess,
}: EditUnitModalProps): React.JSX.Element {
  const { userTitles } = useUserTitles();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      role_name: current.role_name ?? '',
      user_title_id: current.user_title_id ?? '',
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        role_name: current.role_name ?? '',
        user_title_id: current.user_title_id ?? '',
      });
    }
  }, [open, current, reset]);

  const mutation = useMutation({
    mutationFn: (values: Values) =>
      addUserToUnit(accountId, userId, {
        unit_id: unitId,
        role_name: values.role_name,
        user_title_id: values.user_title_id || undefined,
      }),
    onSuccess: () => {
      toast.success('Unit assignment updated');
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update unit assignment');
    },
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      PaperProps={{ component: 'form', onSubmit: handleSubmit((v) => mutation.mutate(v)) }}
    >
      <DialogTitle>Edit {unitLabel}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Controller
            control={control}
            name="role_name"
            render={({ field }) => (
              <FormControl error={Boolean(errors.role_name)} fullWidth>
                <InputLabel>Relationship</InputLabel>
                <Select {...field} label="Relationship">
                  {unitRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role_name && <FormHelperText>{errors.role_name.message}</FormHelperText>}
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="user_title_id"
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Title (optional)</InputLabel>
                <Select {...field} label="Title (optional)">
                  <MenuItem value="">None</MenuItem>
                  {(userTitles ?? []).map((title) => (
                    <MenuItem key={title.id} value={String(title.id)}>
                      {title.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={mutation.isPending} type="submit" variant="contained">
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
